import { Modal, ModalContent } from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useProducts } from '~/common/contexts/products';
import type { SecurityEvent } from '~/common/utils/admin-client/get-security-event-log';
import styles from './SecurityEventLogDetail.css';
import { formatDate, getCategoryLabel } from './utils';

interface SecurityEventLogDetailProps {
  event: SecurityEvent;
  onClose: () => void;
}

const SecurityEventLogDetail: React.FC<SecurityEventLogDetailProps> = ({
  event,
  onClose
}) => {
  const { t } = useTranslation();
  const { getProductBySlug } = useProducts();

  const rawProductSlug = event.data?.productSlug;
  const productSlug =
    typeof rawProductSlug === 'string' ? rawProductSlug : undefined;
  const productName = productSlug
    ? getProductBySlug(productSlug)?.name || productSlug
    : null;

  return (
    <Modal heading={t('Event Details')} onClose={onClose} show>
      <ModalContent>
        <dl className={styles.detailList}>
          <dt>{t('Event ID')}</dt>
          <dd>
            <code>{event.event_id}</code>
          </dd>
          <dt>{t('Enterprise')}</dt>
          <dd>
            {event.enterprise_name || event.enterprise_id || (
              <span className={styles.muted}>&mdash;</span>
            )}
            {event.enterprise_name && event.enterprise_id && (
              <span className={styles.muted}>
                {' '}
                (<code>{event.enterprise_id}</code>)
              </span>
            )}
          </dd>
          <dt>{t('Event Type')}</dt>
          <dd>
            <code>{event.event_type}</code>
          </dd>
          <dt>{t('Category')}</dt>
          <dd>{getCategoryLabel(t, event.category)}</dd>
          <dt>{t('Timestamp')}</dt>
          <dd>{formatDate(event.timestamp)}</dd>
          <dt>{t('Actor')}</dt>
          <dd>
            {event.actor_display_name || t('System')}
            {event.actor_user_id && (
              <span className={styles.muted}>
                {' '}
                [<code>{event.actor_user_id}</code>]
              </span>
            )}
          </dd>
          <dt>{t('IP Address')}</dt>
          <dd>
            {event.actor_ip_address ? (
              <code>{event.actor_ip_address}</code>
            ) : (
              <span className={styles.muted}>&mdash;</span>
            )}
          </dd>
          <dt>{t('Admin Event')}</dt>
          <dd>{event.admin_event ? t('Yes') : t('No')}</dd>
          {productName && (
            <>
              <dt>{t('Product')}</dt>
              <dd>{productName}</dd>
            </>
          )}
          <dt>{t('Data')}</dt>
          <dd>
            <pre className={styles.json}>
              {JSON.stringify(event.data, null, 2)}
            </pre>
          </dd>
        </dl>
      </ModalContent>
    </Modal>
  );
};

export default SecurityEventLogDetail;
